import { ROUND_PHASES } from "@/types"
import type { Phase, Round } from "@/types"

/**
 * Round and phase copy, from the prototype's ROUNDS and phase pickers.
 */

/*
 * Round labels are Title Case, matching the prototype's `ROUNDS[].n`. They read
 * as the names of things — the room is told "we are doing Set the Priorities
 * next", not "we are doing set the priorities next".
 *
 * There is no `title` field. The prototype's was the same string as the label,
 * and the divergent one app-v1 used to carry was never rendered by anything:
 * every round screen has its own heading, in its own copy file.
 */
interface RoundCopy {
  label: string
  objective: string
}

export const ROUND_COPY: Record<Round, RoundCopy> = {
  lobby: {
    label: "Set up",
    objective: "Who's playing, who we're playing for, and how this session runs.",
  },
  pains: {
    label: "Frame the Problem",
    objective: "Everyone writes alone. Then we turn them over together.",
  },
  job: {
    label: "Name the Job",
    objective: "Lock the problem before anyone touches the deck.",
  },
  cards: {
    label: "Explore Opportunities",
    objective: "Select the cards that best fit the problem.",
  },
  priorities: {
    label: "Set the Priorities",
    objective: "One card at a time, out loud. The facilitator moves it.",
  },
  deep: {
    label: "Deep Dive",
    objective: "Four boxes per use case, in their words.",
  },
  wrap: {
    label: "The Debrief",
    objective: "What the room walks away holding.",
  },
}

/**
 * Phase labels, keyed by round because the same phase name means different
 * things in different rounds — `draft` is writing the job, but `pick` in the
 * Cards round is the room choosing and `review` is the facilitator curating.
 */

export const PHASE_COPY: { [R in Round]: Record<(typeof ROUND_PHASES)[R][number], string> } = {
  lobby: { setup: "Set up" },
  pains: { write: "Write", reveal: "Reveal", vote: "Vote", tally: "Tally" },
  job: { draft: "Draft", locked: "Locked" },
  cards: { pick: "Nominate", review: "Carry forward" },
  priorities: { place: "Place" },
  deep: { fill: "Fill" },
  wrap: { summary: "Summary" },
}

export function phaseLabel(round: Round, phase: Phase): string {
  const labels: Record<string, string> = PHASE_COPY[round]
  return labels[phase] ?? phase
}

/**
 * The phases a facilitator can click between, per round — the prototype's phase
 * pickers, and a strict subset of `ROUND_PHASES`.
 *
 * Not every phase is a step someone navigates to. `tally` is reached by
 * revealing the vote and `locked` by locking the job: they are real shared
 * states the room can be in, but they belong to an action, not to a row of
 * chips. The Priorities round's single entry is deliberately inert.
 */
const SWITCHABLE_PHASES: { [R in Round]: readonly (typeof ROUND_PHASES)[R][number][] } = {
  lobby: [],
  pains: ["write", "reveal", "vote"],
  job: [],
  cards: ["pick", "review"],
  priorities: [],
  deep: [],
  wrap: [],
}

export function switchablePhases(round: Round): readonly Phase[] {
  return SWITCHABLE_PHASES[round]
}

/**
 * The rail and the phase chips, in words.
 *
 * Every one of these is BOTH the tooltip and the control's `aria-label` — see
 * `Tooltip`, which is `aria-hidden` precisely so the two cannot drift into
 * saying different things. Which means the locked ones have to explain
 * themselves: "Deep Dive" alone on a pill that refuses to be pressed is a bug
 * report waiting to be filed.
 */
export const NAV_COPY = {
  /** A round the room has been through. */
  done: (label: string) => `${label} — look back, read only`,
  /** The round the room is standing in. */
  live: (label: string) => `${label} — where the room is now`,
  /** A round the rail will not open, for either surface. */
  ahead: (label: string) => `${label} — opens when the room gets there`,
  /** A phase of the round in progress, already been through. */
  donePhase: (label: string) => `${label} — look back, read only`,
  livePhase: (label: string) => `${label} — where the room is now`,
  aheadPhase: (label: string) => `${label} — not open yet`,
  /** Every phase chip, while a FINISHED round is being read back. */
  frozenPhase: (label: string) => `${label} — finished rounds open where they were left`,

  backToLive: "Back to live",
  backToLiveHint: (label: string) => `Rejoin the room on ${label}`,
  /** The strip over a round being read back. */
  reviewing: (label: string) => `Looking back at ${label}`,
  readOnly: "Read only — nothing here can be changed",
  roomIsOn: (label: string) => `The room is on ${label}`,

  /** Parking the session to finish in a follow-up call — see `saveAndPause`. */
  saveAndQuit: "Save & quit",
  saveAndQuitHint: "Save where the room is and pick it up in a follow-up call",
  /** The confirmation step in front of it — the button is easy to misclick, and
   *  it drops the facilitator out of the room. */
  quitTitle: "Save and quit?",
  quitBody:
    "The session is saved where the room is. It stays open until you pick it up again or archive it.",
  quitConfirm: "Save & quit",
  quitCancel: "Keep going",
  /** The share link row in Session settings, copyable to gather the room again. */
  shareLinkLabel: "Session link",
  /** The copy-link control's hover/aria label, and its confirmation. */
  copyLink: "Copy session link",
  copiedLink: "Session link copied",
} as const

/**
 * Which chip to mark as current: the phase itself when it is switchable, else
 * the last switchable phase before it. So the Vote chip stays lit while the room
 * is on `tally` — the room has not left the vote, the tally is just showing.
 * Null when the round has no chips, or nothing precedes the current phase.
 */
export function displayPhase(round: Round, phase: Phase): Phase | null {
  const switchable = switchablePhases(round)
  if (switchable.includes(phase)) return phase

  const all = ROUND_PHASES[round] as readonly Phase[]
  const index = all.indexOf(phase)
  if (index < 0) return null

  for (let i = index - 1; i >= 0; i--) {
    if (switchable.includes(all[i])) return all[i]
  }
  return null
}

const FACILITATOR_CTA: { [R in Round]: Partial<Record<(typeof ROUND_PHASES)[R][number], string>> } =
  {
    lobby: { setup: "Start the session" },
    pains: {
      write: "Reveal pain points",
      reveal: "Open the vote",
      vote: "Reveal",
      // Not "Take it forward": the count recommends, and the next screen is
      // where the room chooses which pain point becomes the job.
      tally: "Choose the problem",
    },
    job: { draft: "Lock the job", locked: "Open the deck" },
    cards: { pick: "Close nominations", review: "To the fit board" },
    priorities: { place: "Wrap up this problem" },
    deep: { fill: "Wrap up this problem" },
    wrap: { summary: "Finish & save" },
  }

/** What the room is told while the facilitator works. The artifact's wording. */
const PARTICIPANT_STATUS: {
  [R in Round]: Partial<Record<(typeof ROUND_PHASES)[R][number], string>>
} = {
  lobby: {},
  pains: { reveal: "Reading pain points", tally: "Counting the votes" },
  job: { draft: "The facilitator is writing the job" },
  cards: { review: "The facilitator is choosing the shortlist" },
  priorities: { place: "The facilitator is placing the cards" },
  deep: { fill: "The facilitator is filling in the detail" },
  wrap: {},
}

const LEADING = "The facilitator is leading"

/**
 * The Priorities round's button does two different things depending on whether
 * the deep dive is on: the same press either opens the dive or closes the job.
 * `FACILITATOR_CTA` is keyed by round and phase only, which cannot express that,
 * so the caller swaps in this label when the next round is the dive.
 */
export const DEEP_DIVE_CTA = "Deep dive"

/**
 * What the Priorities button says while cards are still in the queue.
 *
 * A disabled button whose label is the thing you cannot do yet reads as broken;
 * the prototype instead names the blocker, so the button explains itself without
 * the facilitator having to hover for a tooltip.
 */
export const PRIORITIES_BLOCKED_CTA = "Place every card first"

/** The facilitator's button, or null when there is nowhere left to go. */
export function facilitatorCta(round: Round, phase: Phase): string | null {
  const perPhase: Record<string, string | undefined> = FACILITATOR_CTA[round]
  return perPhase[phase] ?? null
}

/** What a participant reads in the same place. */
export function participantStatus(round: Round, phase: Phase): string {
  const perPhase: Record<string, string | undefined> = PARTICIPANT_STATUS[round]
  return perPhase[phase] ?? LEADING
}
